import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAdminProducts } from '@/services/products';
import { getPublicPlansByProduct } from '@/services/plans';
import { listAdminCustomers } from '@/services/users';
import type { Product } from '@/domain/types';
import type { ProductPlan } from '@/domain/types';
import type { AdminCustomer } from '@/services/users';

interface CreateResult {
  order_ref: string;
  license_key: string;
  customer_name: string;
  customer_email: string;
  product_name: string;
  plan_name: string;
}

export default function AdminOrderNew() {
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [plans, setPlans] = useState<ProductPlan[]>([]);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CreateResult | null>(null);

  const [customerEmail, setCustomerEmail] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState<AdminCustomer[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [productId, setProductId] = useState('');
  const [planId, setPlanId] = useState('');
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('paid');

  useEffect(() => {
    async function load() {
      try {
        const [p, c] = await Promise.all([
          listAdminProducts(),
          listAdminCustomers(),
        ]);
        setProducts(p);
        setCustomers(c);
      } catch {
        setError('Failed to load form data.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!productId) { setPlans([]); return; }
    getPublicPlansByProduct(productId).then(setPlans).catch(() => setPlans([]));
  }, [productId]);

  const selectedPlan = plans.find((pl) => pl.id === planId);

  const handleEmailChange = (val: string) => {
    setCustomerEmail(val);
    if (val.length >= 2) {
      const match = customers.filter((c) =>
        c.email.toLowerCase().includes(val.toLowerCase()) ||
        c.name.toLowerCase().includes(val.toLowerCase()),
      );
      setCustomerSuggestions(match.slice(0, 5));
      setShowSuggestions(match.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectCustomer = (c: AdminCustomer) => {
    setCustomerEmail(c.email);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerEmail || !productId || !planId) return;

    setSaving(true);
    setError('');
    setResult(null);

    try {
      const { supabase } = await import('@/lib/supabase');
      const session = (await supabase!.auth.getSession()).data.session;
      const token = session?.access_token;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: customerEmail,
            productId,
            planId,
            amount: amount ? Number(amount) : null,
            status,
          }),
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create order.');
      setResult(data.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create order.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-surface-400 text-xl" />
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-lg mx-auto space-y-5">
        <div className="bg-white rounded-xl border border-green-200 p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-green-600 text-2xl" />
          </div>
          <h2 className="text-lg font-bold text-surface-900 mb-1">Order Created</h2>
          <p className="text-sm text-surface-500 mb-5">Order #{result.order_ref} has been created successfully.</p>

          <div className="bg-surface-50 rounded-xl p-4 text-left space-y-2 mb-5">
            <div className="flex justify-between text-sm"><span className="text-surface-500">Customer</span><span className="font-medium text-surface-900">{result.customer_name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-surface-500">Email</span><span className="font-medium text-surface-900">{result.customer_email}</span></div>
            <div className="flex justify-between text-sm"><span className="text-surface-500">Product</span><span className="font-medium text-surface-900">{result.product_name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-surface-500">Plan</span><span className="font-medium text-surface-900">{result.plan_name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-surface-500">License Key</span><span className="font-medium font-mono text-surface-900 text-xs">{result.license_key}</span></div>
          </div>

          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/admin/orders')} className="px-5 py-2.5 rounded-lg border border-surface-300 text-surface-700 text-sm font-medium hover:bg-surface-50 transition-colors">
              View All Orders
            </button>
            <button onClick={() => setResult(null)} className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors">
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">New Order</h1>
        <p className="text-sm text-surface-500 mt-1">Manually create an order for a customer.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          <i className="ri-error-warning-line shrink-0" /> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-surface-200 p-5 md:p-6 space-y-5">
        {/* Customer */}
        <div className="relative">
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Customer Email</label>
          <input
            type="email"
            value={customerEmail}
            onChange={(e) => handleEmailChange(e.target.value)}
            onFocus={() => customerEmail.length >= 2 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="customer@example.com"
            required
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
          {showSuggestions && (
            <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white rounded-xl border border-surface-200 shadow-lg overflow-hidden">
              {customerSuggestions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectCustomer(c)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-surface-50 transition-colors"
                >
                  <span className="font-medium text-surface-900">{c.name}</span>
                  <span className="text-surface-400">{c.email}</span>
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-surface-400 mt-1">If the email doesn't exist, an account will be created automatically.</p>
        </div>

        {/* Product */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Product</label>
          <select
            value={productId}
            onChange={(e) => { setProductId(e.target.value); setPlanId(''); }}
            required
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          >
            <option value="">Select a product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Plan */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Plan</label>
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          >
            <option value="">Select a plan</option>
            {plans.map((pl) => (
              <option key={pl.id} value={pl.id}>
                {pl.name} — ${pl.price} ({pl.billingType === 'subscription' ? `${pl.intervalCount} ${pl.intervalUnit}` : 'One-time'})
              </option>
            ))}
          </select>
        </div>

        {/* Amount & Status */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">
              Amount {selectedPlan && <span className="text-surface-400 font-normal">(default: ${selectedPlan.price})</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={selectedPlan ? String(selectedPlan.price) : '0.00'}
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
            >
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/admin/orders')}
            className="px-5 py-2.5 rounded-lg border border-surface-300 text-surface-700 text-sm font-medium hover:bg-surface-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !customerEmail || !productId || !planId}
            className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center gap-2"
          >
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {saving ? 'Creating…' : 'Create Order'}
          </button>
        </div>
      </form>
    </div>
  );
}
